/**
 * `<RichTextToolbar>` — the toolbar surface for the Tiptap-based rich pane.
 *
 * Why this lives separately (2026-05-18):
 *
 * The old MDXEditor toolbar was assembled out of vendor-provided React
 * components (`UndoRedo`, `BoldItalicUnderlineToggles`, `BlockTypeSelect`,
 * etc.) — convenient at the time but coupled the toolbar shape to MDXEditor's
 * internals. With Tiptap we control the toolbar entirely, which means:
 *
 *   - The buttons look like the rest of our shell (uses the same icon set
 *     as the global UI — `lucide-react` — instead of MDXEditor's vendor icons)
 *   - We add / remove buttons without waiting for a vendor release
 *   - The active-state reading is direct (`editor.isActive(name)`) rather than
 *     a Lexical-state subscription through MDXEditor's `Cell` system
 *
 * The button set mirrors the surface MDXEditor exposed exactly so users see
 * no functional regression:
 *
 *   Inline:    Bold · Italic · Underline · Strikethrough · Sup · Sub · Code
 *   Block:     Heading select (paragraph, h1-h6, blockquote)
 *   Inserts:   Link · Image · Table · Horizontal rule · Code block
 *   Lists:     Bulleted · Numbered · Task (checkbox)
 *   History:   Undo · Redo
 *
 * Link and image inserts use the branded `usePromptModal` dialog
 * (facelift-v3 guide Phase 6) — replacing the original `window.prompt`
 * popups with the in-app `<Modal>` idiom. The `allowEmpty` option keeps the
 * "clear link" (empty URL) and "optional alt text" affordances working.
 */

import { useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import type { LucideIcon } from 'lucide-react';
import {
  Workflow,
  GanttChartSquare,
  ChevronDown,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Superscript as SuperscriptIcon,
  Subscript as SubscriptIcon,
  Code,
  List,
  ListOrdered,
  CheckSquare,
  Link as LinkIcon,
  Image as ImageIcon,
  Table as TableIcon,
  Minus,
  FileCode,
  Undo2,
  Redo2,
  Highlighter,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Quote,
} from 'lucide-react';
import { usePromptModal } from '@/components';
import { FontColorPicker } from './FontColorPicker';

interface RichTextToolbarProps {
  editor: Editor;
}

type BlockTypeValue =
  | 'paragraph'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'blockquote';

const BLOCK_TYPE_OPTIONS: Array<{ value: BlockTypeValue; label: string }> = [
  { value: 'paragraph', label: 'Paragraph' },
  { value: 'h1', label: 'Heading 1' },
  { value: 'h2', label: 'Heading 2' },
  { value: 'h3', label: 'Heading 3' },
  { value: 'h4', label: 'Heading 4' },
  { value: 'h5', label: 'Heading 5' },
  { value: 'h6', label: 'Heading 6' },
  { value: 'blockquote', label: 'Quote' },
];

/** Read the editor's currently-active block type as one of our enum values. */
function readActiveBlockType(editor: Editor): BlockTypeValue {
  if (editor.isActive('blockquote')) return 'blockquote';
  for (let i = 1; i <= 6; i++) {
    if (editor.isActive('heading', { level: i }))
      return `h${i}` as BlockTypeValue;
  }
  return 'paragraph';
}

/** Apply the selected block type via the appropriate Tiptap command. */
function applyBlockType(editor: Editor, value: BlockTypeValue): void {
  const chain = editor.chain().focus();
  if (value === 'paragraph') {
    chain.setParagraph().run();
    return;
  }
  if (value === 'blockquote') {
    chain.toggleBlockquote().run();
    return;
  }
  const level = parseInt(value.slice(1), 10) as 1 | 2 | 3 | 4 | 5 | 6;
  chain.toggleHeading({ level }).run();
}

interface ToolbarButtonProps {
  /** A lucide-react icon component. Use the package's `LucideIcon` type so
   *  the props match the ForwardRefExoticComponent shape lucide ships. */
  icon: LucideIcon;
  label: string;
  isActive?: boolean;
  isDisabled?: boolean;
  onClick: () => void;
}

function ToolbarButton({
  icon: Icon,
  label,
  isActive = false,
  isDisabled = false,
  onClick,
}: ToolbarButtonProps): JSX.Element {
  return (
    <button
      type="button"
      className={`rich-toolbar__btn${isActive ? ' rich-toolbar__btn--active' : ''}`}
      onMouseDown={(e) => e.preventDefault()} // keep focus in editor
      onClick={onClick}
      disabled={isDisabled}
      title={label}
      aria-label={label}
      aria-pressed={isActive}
    >
      <Icon size={16} aria-hidden="true" />
    </button>
  );
}

function ToolbarSeparator(): JSX.Element {
  return <span className="rich-toolbar__sep" aria-hidden />;
}

/** Starter flowchart — canonical shapes + a colour legend so a new diagram is presentation-ready. */
const FLOWCHART_TEMPLATE = `flowchart LR
    classDef source fill:#dcfce7,stroke:#16a34a,color:#14532d;
    classDef process fill:#dbeafe,stroke:#2563eb,color:#1e3a8a;
    classDef store fill:#fef9c3,stroke:#ca8a04,color:#713f12;
    A[/Input/]:::source -->|data| B[Process step]:::process
    B -->|save| C[(Datastore)]:::store`;

/** Starter gantt — a couple of dated tasks + a dependency + a milestone to drag around. */
const GANTT_TEMPLATE = `gantt
    title New plan
    dateFormat YYYY-MM-DD
    axisFormat %b %d
    excludes weekends
    section Phase 1
        First task :active, t1, 2026-07-08, 7d
        Second task :t2, after t1, 5d
        Milestone :milestone, m1, after t2, 0d`;

/** The "Insert diagram" dropdown — a button that offers a flowchart or a gantt block. */
function DiagramMenu({ editor }: { editor: Editor }): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const insert = (source: string): void => {
    editor.chain().focus().insertContent({ type: 'mermaidBlock', attrs: { source } }).run();
    setOpen(false);
  };

  return (
    <div className="rich-toolbar__menu" ref={ref}>
      <button
        type="button"
        className={`rich-toolbar__btn rich-toolbar__btn--menu${open ? ' rich-toolbar__btn--active' : ''}`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((o) => !o)}
        title="Insert diagram"
        aria-label="Insert diagram"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Workflow size={16} aria-hidden="true" />
        <ChevronDown size={12} aria-hidden="true" />
      </button>
      {open && (
        <div className="rich-toolbar__dropdown" role="menu">
          <button
            type="button"
            role="menuitem"
            className="rich-toolbar__dropdown-item"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => insert(FLOWCHART_TEMPLATE)}
          >
            <Workflow size={15} aria-hidden="true" />
            <span>Flowchart diagram</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="rich-toolbar__dropdown-item"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => insert(GANTT_TEMPLATE)}
          >
            <GanttChartSquare size={15} aria-hidden="true" />
            <span>Gantt chart</span>
          </button>
        </div>
      )}
    </div>
  );
}

export function RichTextToolbar({ editor }: RichTextToolbarProps): JSX.Element {
  const activeBlockType = readActiveBlockType(editor);
  // facelift-v3 guide Phase 6: branded prompt modals replace window.prompt
  // for the link + image insertion flows.
  const { modal, prompt } = usePromptModal();

  const promptForLink = async (): Promise<void> => {
    const previousUrl = editor.getAttributes('link').href as string | undefined;
    const url = await prompt({
      eyebrow: 'Link',
      title: 'Link URL',
      initial: previousUrl ?? 'https://',
      placeholder: 'https://example.com',
      confirmLabel: 'Apply',
      allowEmpty: true, // empty clears the link
    });
    if (url === null) return; // user cancelled
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: url })
      .run();
  };

  const promptForImage = async (): Promise<void> => {
    const url = await prompt({
      eyebrow: 'Image',
      title: 'Image URL',
      initial: 'https://',
      placeholder: 'https://example.com/image.png',
      confirmLabel: 'Next',
    });
    if (url === null || url === '') return;
    const alt = await prompt({
      eyebrow: 'Image',
      title: 'Alt text',
      placeholder: 'Describe the image (optional)',
      confirmLabel: 'Insert',
      allowEmpty: true,
    });
    if (alt === null) return; // cancelled the alt step
    editor.chain().focus().setImage({ src: url, alt }).run();
  };

  const insertTable = (): void => {
    editor
      .chain()
      .focus()
      .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
      .run();
  };

  return (
    <>
    {modal}
    <div className="rich-toolbar" role="toolbar" aria-label="Editor toolbar">
      <ToolbarButton
        icon={Undo2}
        label="Undo"
        onClick={() => editor.chain().focus().undo().run()}
        isDisabled={!editor.can().undo()}
      />
      <ToolbarButton
        icon={Redo2}
        label="Redo"
        onClick={() => editor.chain().focus().redo().run()}
        isDisabled={!editor.can().redo()}
      />
      <ToolbarSeparator />

      <ToolbarButton
        icon={Bold}
        label="Bold"
        isActive={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      />
      <ToolbarButton
        icon={Italic}
        label="Italic"
        isActive={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      />
      <ToolbarButton
        icon={UnderlineIcon}
        label="Underline"
        isActive={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      />
      <ToolbarButton
        icon={Strikethrough}
        label="Strikethrough"
        isActive={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      />
      <ToolbarButton
        icon={SuperscriptIcon}
        label="Superscript"
        isActive={editor.isActive('superscript')}
        onClick={() => editor.chain().focus().toggleSuperscript().run()}
      />
      <ToolbarButton
        icon={SubscriptIcon}
        label="Subscript"
        isActive={editor.isActive('subscript')}
        onClick={() => editor.chain().focus().toggleSubscript().run()}
      />
      <ToolbarButton
        icon={Code}
        label="Inline code"
        isActive={editor.isActive('code')}
        onClick={() => editor.chain().focus().toggleCode().run()}
      />
      <ToolbarButton
        icon={Highlighter}
        label="Highlight"
        isActive={editor.isActive('highlight')}
        onClick={() => editor.chain().focus().toggleHighlight().run()}
      />
      {/* Font colour — an in-app popover (preset palette + last-8 recents +
          custom + clear) following the app design language, replacing the
          bare OS-chrome native colour input. */}
      <FontColorPicker editor={editor} />
      <ToolbarSeparator />

      <select
        className="rich-toolbar__select"
        value={activeBlockType}
        onChange={(e) =>
          applyBlockType(editor, e.target.value as BlockTypeValue)
        }
        onMouseDown={(e) => e.stopPropagation()}
        aria-label="Block type"
      >
        {BLOCK_TYPE_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <ToolbarSeparator />

      <ToolbarButton
        icon={LinkIcon}
        label="Link"
        isActive={editor.isActive('link')}
        onClick={() => void promptForLink()}
      />
      <ToolbarButton
        icon={ImageIcon}
        label="Image"
        onClick={() => void promptForImage()}
      />
      <ToolbarSeparator />

      <ToolbarButton
        icon={List}
        label="Bulleted list"
        isActive={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      />
      <ToolbarButton
        icon={ListOrdered}
        label="Numbered list"
        isActive={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      />
      <ToolbarButton
        icon={CheckSquare}
        label="Task list"
        isActive={editor.isActive('taskList')}
        onClick={() => editor.chain().focus().toggleTaskList().run()}
      />
      <ToolbarButton
        icon={Quote}
        label="Quote block"
        isActive={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      />
      <ToolbarSeparator />

      <ToolbarButton
        icon={AlignLeft}
        label="Align left"
        isActive={editor.isActive({ textAlign: 'left' })}
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
      />
      <ToolbarButton
        icon={AlignCenter}
        label="Align center"
        isActive={editor.isActive({ textAlign: 'center' })}
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
      />
      <ToolbarButton
        icon={AlignRight}
        label="Align right"
        isActive={editor.isActive({ textAlign: 'right' })}
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
      />
      <ToolbarButton
        icon={AlignJustify}
        label="Justify"
        isActive={editor.isActive({ textAlign: 'justify' })}
        onClick={() => editor.chain().focus().setTextAlign('justify').run()}
      />
      <ToolbarSeparator />

      <ToolbarButton
        icon={TableIcon}
        label="Insert table"
        onClick={insertTable}
      />
      <ToolbarButton
        icon={Minus}
        label="Horizontal rule"
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
      />
      <ToolbarButton
        icon={FileCode}
        label="Code block"
        isActive={editor.isActive('codeBlock')}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
      />
      <DiagramMenu editor={editor} />
    </div>
    </>
  );
}
